import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { submitEvent } from "@/lib/api";

const TILES = [
  {
    title: "Corporate Dinners",
    description: "Boardroom-to-banquet hospitality with views over the Gower countryside.",
    image:
      "https://customer-assets.emergentagent.com/job_gower-golf-portal/artifacts/oah6xo30_ChatGPT%20Image%20May%2030%2C%202026%2C%2009_44_23%20AM.png",
    span: "lg:col-span-7 lg:row-span-2",
  },
  {
    title: "Golf Societies",
    description: "Bespoke golf society packages tailored to your group. Typical packages include coffee & bacon bap on arrival, 18 holes of golf and a post-round meal. Enquire below for your personalised quotation.",
    image:
      "https://images.unsplash.com/photo-1645143617907-1378a279eef2?fm=jpg&q=85&w=900&auto=format&fit=crop",
    span: "lg:col-span-5",
  },
  {
    title: "Weddings",
    description: "Swansea's welcoming wedding venue, framed by the Gower Peninsula.",
    image:
      "https://customer-assets.emergentagent.com/job_gower-golf-portal/artifacts/2kz0dv8l_ChatGPT%20Image%20May%2030%2C%202026%2C%2010_21_50%20AM.png",
    span: "lg:col-span-5",
  },
];

export default function Events() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    event_type: "",
    date: "",
    guest_count: 0,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: k === "guest_count" ? Number(e.target.value) : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type || !form.date || !form.guest_count) {
      toast.error("Please complete all event inquiry fields.");
      return;
    }
    setLoading(true);
    try {
      await submitEvent(form);
      toast.success("Event inquiry received. Our events team will respond within 24 hours.");
      setForm({ name: "", email: "", event_type: "", date: "", guest_count: 0, message: "" });
    } catch (err) {
      toast.error("Unable to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="events" data-testid="events-section" className="bg-white py-24 md:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <span className="section-eyebrow">03 · Events, Weddings &amp; Functions</span>
            <h2 className="font-serif-luxury text-4xl md:text-5xl lg:text-6xl text-[#0F4C3A] mt-5 leading-[1.05] tracking-tight">
              Swansea's Premier
              <span className="block italic text-gold-metallic">Wedding, Golf &amp; Stay Venue</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[#4A4A4A] leading-relaxed">
              From corporate dinners and golf societies to elegant weddings overlooking the Gower
              Peninsula, every gathering at The Gower Golf Club is hosted with quiet British elegance.
            </p>
          </div>
        </div>

        {/* Bento split grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-2 gap-6 lg:gap-8 mt-14 lg:h-[680px]">
          {TILES.map((t, i) => (
            <article
              key={t.title}
              data-testid={`event-tile-${i}`}
              className={`relative image-frame ${t.span} group min-h-[260px] lg:min-h-0`}
            >
              <img src={t.image} alt={t.title} className="w-full h-full object-cover absolute inset-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F4C3A]/85 via-[#0F4C3A]/20 to-transparent" />
              <div className="absolute inset-0 p-7 md:p-9 flex flex-col justify-end text-white">
                <p className="section-eyebrow text-[#E6D5A8]">Hosted at the Gower</p>
                <h3 className="font-serif-luxury text-3xl md:text-4xl mt-2">{t.title}</h3>
                <p className="text-sm md:text-base text-white/85 mt-3 max-w-md leading-relaxed">{t.description}</p>
                <div className="mt-5 h-px w-12 bg-[#CBA153]" />
              </div>
            </article>
          ))}
        </div>

        {/* Enquire form */}
        <div className="mt-20 bg-[#FAFAFA] border border-[#CBA153]/30 p-8 md:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="section-eyebrow">Enquire</span>
              <h3 className="font-serif-luxury text-3xl md:text-4xl text-[#0F4C3A] mt-4 leading-tight">
                Begin your Gower event
                <span className="italic text-gold-metallic"> story.</span>
              </h3>
              <p className="text-sm text-[#4A4A4A] mt-5 leading-relaxed">
                Submissions are routed directly to{" "}
                <span className="text-[#0F4C3A] font-medium">info@gowergolf.com</span>. Our events
                concierge replies within one business day.
              </p>
            </div>

            <form onSubmit={onSubmit} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="event-form">
              <div>
                <label className="luxury-label" htmlFor="ev-name">Name</label>
                <input id="ev-name" data-testid="event-input-name" className="luxury-input" value={form.name} onChange={update("name")} required />
              </div>
              <div>
                <label className="luxury-label" htmlFor="ev-email">Email</label>
                <input id="ev-email" data-testid="event-input-email" type="email" className="luxury-input" value={form.email} onChange={update("email")} required />
              </div>
              <div>
                <label className="luxury-label" htmlFor="ev-type">Event Type</label>
                <select
                  id="ev-type"
                  data-testid="event-input-type"
                  className="luxury-input bg-transparent"
                  value={form.event_type}
                  onChange={update("event_type")}
                  required
                >
                  <option value="">Select an event type</option>
                  <option value="Corporate Dinner">Corporate Dinner</option>
                  <option value="Golf Society">Golf Society Day</option>
                  <option value="Wedding">Wedding</option>
                  <option value="Private Function">Private Function</option>
                </select>
              </div>
              <div>
                <label className="luxury-label" htmlFor="ev-date">Date</label>
                <input id="ev-date" data-testid="event-input-date" type="date" className="luxury-input" value={form.date} onChange={update("date")} required />
              </div>
              <div>
                <label className="luxury-label" htmlFor="ev-count">Guest Count</label>
                <input id="ev-count" data-testid="event-input-count" type="number" min="1" className="luxury-input" value={form.guest_count || ""} onChange={update("guest_count")} required />
              </div>
              <div>
                <label className="luxury-label" htmlFor="ev-msg">Additional Details</label>
                <input id="ev-msg" data-testid="event-input-msg" className="luxury-input" value={form.message} onChange={update("message")} placeholder="Tell us about your event" />
              </div>
              <div className="md:col-span-2 mt-2">
                <button data-testid="event-submit-btn" type="submit" disabled={loading} className="ggc-btn-primary disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : "Enquire About Events"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
